import { isEmpty } from 'lodash';

function compareValues(comparison, conditionalValue, value) {
  // compare the conditionalValue to the value
  switch (comparison) {
    case 'IS':
      return conditionalValue === value;
    case 'IS_NOT':
      return !isEmpty(conditionalValue) && conditionalValue !== value;
    case 'IS_EMPTY':
      return isEmpty(conditionalValue);
    case 'IS_NOT_EMPTY':
      return !isEmpty(conditionalValue);
    case 'CONTAINS':
      return conditionalValue.includes(value);
    case 'DOES_NOT_CONTAIN':
      return !isEmpty(conditionalValue) && !conditionalValue.includes(value);
  }
}

/**
 * Returns true if the variant should be shown, false if it should be hidden
 * @param {Object} variant - the variant to check
 * @param {Object} formVariants - the variants object from the form
 * @param {Object} productVariants - the variants object from the product
 * @param {Object} query - the query object which may contain `show_variants` as a comma separated list of variant references to show
 * @returns {Boolean}
 */
export default function checkIsVariantEnabled(
  variant,
  formVariants,
  productVariants = [],
  visited = new Set(),
) {
  // if showHideLogic is not set, return the variant
  if (!variant.showHideLogic) return true;
  if (!variant.showHideLogic.enabled) return true;

  // use the variant's showHideLogic to determine if the variant should be shown
  // showHideLogic contains an element, comparison operator and value
  const { element, comparison, value } = variant.showHideLogic;

  const comparisonElement = productVariants.find(variant => variant._id === element.elementId);

  if (!comparisonElement) return false;

  // show hide logic can’t depend on a quantity select variant
  if (comparisonElement.selectionType === 'quantity') return false;

  // get the value of the element from the formVariants
  const formVariantOption = formVariants[element.elementId][0];

  if (comparison === 'IS_EMPTY') {
    return compareValues(comparison, formVariantOption, value);
  }

  if (!formVariantOption) return false;

  // check if the formVariantOption has quantity
  const conditionalValue =
    formVariantOption?.quantity > 0 ? formVariantOption?.optionId : undefined;

  // if the value of the element is not set, don't show the variant
  if (conditionalValue === undefined) return false;

  // check for circular dependencies
  if (visited.has(variant._id)) {
    return false;
  }

  visited.add(variant._id);

  // check if the comparison element is visible
  const isComparisonElementVisible = checkIsVariantEnabled(
    comparisonElement,
    formVariants,
    productVariants,
    visited,
  );

  // if this is a dependency check, and the comparison element is not visible, don't show the variant
  if (!isComparisonElementVisible) return false;

  const isVisible = compareValues(comparison, conditionalValue, value);

  return isVisible;
}
